import React, { useState, useRef, useCallback } from 'react';
import '../App.css';

const UIOverlay = ({ onOpenContact, onOpenProjects, onOpenAbout, onOpenConsultation }) => {
  const [activePage, setActivePage] = useState(0);
  const scrollRef = useRef(null);
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const consultRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    const h = container.clientHeight;
    if (scrollTop < h * 0.6) {
      setActivePage(0);
    } else if (scrollTop < h * 1.6) {
      setActivePage(1);
    } else {
      setActivePage(2);
    }
  }, []);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ui-layer" ref={scrollRef} onScroll={handleScroll}>

      {/* ── Page-dot navigator ── */}
      <div className="page-dots">
        <div
          className={`page-dot ${activePage === 0 ? 'active' : ''}`}
          onClick={() => scrollTo(heroRef)}
          title="Overview"
        />
        <div
          className={`page-dot ${activePage === 1 ? 'active' : ''}`}
          onClick={() => scrollTo(servicesRef)}
          title="Services"
        />
        <div
          className={`page-dot ${activePage === 2 ? 'active' : ''}`}
          onClick={() => scrollTo(consultRef)}
          title="Consultation"
        />
      </div>

      {/* ═══════════════════════════════════════════
          NAVIGATION
      ═══════════════════════════════════════════ */}
      <nav>
        <div className="logo">Protekar_</div>
        <div className="nav-links">
          {/* <a href="#solutions" onClick={(e) => { e.preventDefault(); onOpenProjects(); }}>Solutions</a>
          <a href="#platform" onClick={(e) => { e.preventDefault(); onOpenAbout(); }}>Platform</a> */}
          {/* <a
            href="#consultation"
            className="nav-cta"
            onClick={(e) => { e.preventDefault(); onOpenConsultation(); }}
          >
            Book a Consultation
          </a> */}
          <a href="#contact" onClick={(e) => { e.preventDefault(); onOpenContact(); }}>Contact</a>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════
          PAGE 1 / HERO
      ═══════════════════════════════════════════ */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero">
          <div className="label">System Ready</div>

          <h1>
            Embedded<br />
            <span>Intelligence.</span>
          </h1>

          <p>
            Specialised industries require inference systems built from the
            ground up. We develop custom models, autonomous agent pipelines,
            and precision prediction architectures tuned to the operational
            constraints, data characteristics, and compliance requirements of
            your specific domain.
          </p>

          <div className="hero-cta">
            <button
              className="btn btn-primary"
              onClick={onOpenConsultation}
            >
              Book a Consultation
            </button>
            <button
              className="btn btn-secondary"
              onClick={onOpenContact}
            >
              Contact Us
            </button>
          </div>
        </div>

        {activePage === 0 && (
          <div className="scroll-hint">Scroll</div>
        )}
      </section>

      {/* ═══════════════════════════════════════════
          PAGE 2 / SERVICES
      ═══════════════════════════════════════════ */}
      <section className="services-section" ref={servicesRef}>
        <div className="section-header">
          <div className="label">Capabilities</div>
          <h2>
            What We<br /><span>Build.</span>
          </h2>
          <p className="section-intro">
            Each engagement begins with deep domain analysis. We develop
            inference systems calibrated to your operational environment, your
            data characteristics, and the constraints that define your
            industry.
          </p>
        </div>

        <div className="services-grid">

          {/* Card 1 */}
          <div className="service-card">
            <div className="service-card-image">
              <img src="./scientists.png" alt="Domain-Specific Model Development" />
            </div>
            <div className="service-card-body">
              <div className="service-tag">Inference Engineering</div>
              <h3>Domain-Specific Model Development</h3>
              <p>
                We develop inference architectures grounded in your operational
                data and domain constraints. Engagements cover the full
                delivery stack: training pipelines, evaluation frameworks, and
                deployment-ready artefacts built to production standards from
                the first iteration.
              </p>
            </div>
          </div>

          {/* Card 2 */}
          <div className="service-card">
            <div className="service-card-image">
              <img src="./pipeline_2.png" alt="Agentic Systems" />
            </div>
            <div className="service-card-body">
              <div className="service-tag">Autonomous Systems</div>
              <h3>Agentic Solutions</h3>
              <p>
                We design multi-agent systems capable of autonomous reasoning,
                tool use, and sequential decision execution within
                well-defined operational boundaries. These systems are suited
                to complex, high-volume workflows where structured oversight
                is required and manual handling at scale is impractical.
              </p>
            </div>
          </div>

          {/* Card 3 */}
          <div className="service-card">
            <div className="service-card-image">
              <img src="./computercables.png" alt="Local Inference Integration" />
            </div>
            <div className="service-card-body">
              <div className="service-tag">On-Premise &amp; Edge</div>
              <h3>Local Inference Integration</h3>
              <p>
                We integrate high-performance inference directly into your
                infrastructure: on-premises servers, edge devices, and
                air-gapped environments. Deployments preserve data sovereignty
                and are optimised for the lowest practical latency achievable
                on your hardware.
              </p>
            </div>
          </div>

          {/* Card 4 */}
          <div className="service-card">
            <div className="service-card-image">
              <img src="./kyoto.png" alt="Prediction Systems" />
            </div>
            <div className="service-card-body">
              <div className="service-tag">Predictive Intelligence</div>
              <h3>Prediction &amp; Forecasting Systems</h3>
              <p>
                We implement predictive systems for demand forecasting,
                anomaly detection, failure anticipation, and decision support.
                Models are built on current methodology and calibrated to the
                statistical characteristics of your operational data.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PAGE 3 / CONSULTATION
      ═══════════════════════════════════════════ */}
      <section className="consult-section" ref={consultRef}>
        <div className="section-header">
          <div className="label">Next Step</div>
          <h2>
            From C-Suite to<br /><span>Engineering Teams.</span>
          </h2>
        </div>

        <div className="consult-content">

          <div className="consult-text">
            <p>
              Whether you are mapping the strategic landscape of AI for your
              organisation or facing a concrete implementation challenge that
              requires specialist input, our consultations are structured to
              deliver technical clarity and an honest assessment of what is
              feasible.
            </p>
            <p>
              We operate across precision verticals: industrial automation,
              medical devices, energy systems, logistics, financial modelling,
              and defence. Organisations in demanding sectors with requirements
              that sit outside the portfolio of standard vendors are the
              clients we work with.
            </p>
            <p>
              Engagements take several forms: a single strategic briefing for
              executive stakeholders, a technical deep-dive for engineering
              leadership, an ongoing advisory arrangement, or full-cycle
              delivery from scoping through deployment. The intake process is
              structured and scoped to your situation, with no commitment
              required to begin.
            </p>

            <div className="consult-cta">
              <button
                className="btn btn-primary"
                onClick={onOpenConsultation}
              >
                Book a Consultation
              </button>
              <span className="consult-cta-note">
                We respond within one business day.
              </span>
            </div>
          </div>

          <div className="consult-image">
            <img src="./creatives_3.png" alt="Protekar Consultation" />
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════ */}
      <div className="stats">
        <div className="stat-item">
          <span>Latency</span>
          {'<'} 2ms
        </div>
        <div className="stat-item">
          <span>Design</span>
          protekar.eu
        </div>
        <div className="stat-item">
          <span>Status</span>
          Online
        </div>
      </div>

    </div>
  );
};

export default UIOverlay;
